import { useCallback } from 'react';
import { useContextSelector } from 'use-context-selector';

import { EditorContext } from '../contexts';
import { DocumentNode, DocumentPath } from '../types';
import { removeNode, setNode } from '../utils/node';

export interface EditorNodeProps {
  isSelected: boolean;
  isChildSelected: boolean;
  isHovered: boolean;
  displayMode: 'view' | 'edit';
  displayDate?: Date;
  onSelect: () => void;
  onHover: (path: DocumentPath) => void;
  onUnhover: () => void;
  onChange: (node: DocumentNode) => void;
  onRemove: () => void;
}

export function useEditorNode(
  path: number[] = [],
): EditorNodeProps {
  const displayMode = useContextSelector(
    EditorContext,
    (context) => context?.state.displayMode ?? 'view',
  );
  const displayDate = useContextSelector(
    EditorContext,
    (context) => context?.state?.displayDate,
  );
  const isSelected = useContextSelector(
    EditorContext,
    (context) => (context?.state != null
      ? context.state.cursor.length === path.length &&
        path.every((v, i) => context.state.cursor[i] === v)
      : false),
  );
  const isChildSelected = useContextSelector(
    EditorContext,
    (context) => (context?.state != null
      ? path.every((v, i) => context.state.cursor[i] === v)
      : false),
  );
  const isHovered = useContextSelector(
    EditorContext,
    (context) => {
      if (!context) { return false; }
      if (!context.state.tempCursor) { return false; }
      return context.state.tempCursor.length === path.length &&
        context.state.tempCursor.every((v, i) => v === path[i]);
    },
  );
  const setState = useContextSelector(
    EditorContext,
    (context) => (context != null
      ? context.setState
      : () => { /* noop */ }),
  );
  const onSelect = useCallback(() => setState((state) => ({
    ...state,
    cursor: path,
  })), [setState, path]);
  const onHover = useCallback((path: DocumentPath) => {
    setState((state) => ({ ...state, tempCursor: path }));
  }, []);
  const onUnhover = useCallback(() => {
    setState(({ tempCursor, ...state }) => state);
  }, []);
  const onChange = useCallback((node: DocumentNode<Record<string, unknown>>) => setState((state) => ({
    ...state,
    cursor: path,
    document: setNode(state.document, path, node),
  })), [setState, path]);
  const onRemove = useCallback(() => setState((state) => ({
    ...state,
    document: removeNode(state.document, path),
  })), [setState, path]);

  return {
    isSelected,
    isChildSelected,
    isHovered,
    displayMode,
    displayDate,
    onSelect,
    onHover,
    onUnhover,
    onChange,
    onRemove,
  };
}

